'use client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchOffers } from '@eliadmin/core/src/api/jobportal/offers-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildJobOffersQuery } from '@eliadmin/core/src/utils/jobportal-offers-query';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { fetchCompanies } from '@eliadmin/core/src/api/jobportal-api';
import { JobOfferCardSmall } from '../../components/jobportal/job-offer-card-small';
import { JobOfferCardSmallSkeleton } from '../../components/jobportal/job-offer-card-small-skeleton';
import { CompanyCardSmall } from '../../components/jobportal/company-card-small';
import { CompanyCardSmallSkeleton } from '../../components/jobportal/company-card-small-skeleton';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GoArrowRight } from 'react-icons/go';
import Link from '@eliadmin/core/src/components/link';
import { PortalContentError } from '../../components/profile/portal-content-loader';

export const Page = () => {
  const locale = useLocaleStore(state => state.currentLocale);
  const { me, isInitialized } = useCustomerStore();

  const {
    data: offers,
    isLoading: isLoadingOffers,
    isError: offersError,
    refetch: refetchOffers,
  } = useQuery({
    queryKey: ['dual-education-offers', locale, me?.authToken ?? null],
    queryFn: () =>
      fetchOffers({
        locale,
        authToken: me?.authToken,
        query: {
          'order[]': ['desc', 'desc'],
          'orderBy[]': ['topped_up_to', 'published_at'],
          results: 8,
          page: 1,
          ...buildJobOffersQuery({
            is_paid: true,
            dual_education: true,
          }),
        },
      }),
    enabled: isInitialized,
  });

  const {
    data: companiesPages,
    hasNextPage: hasMoreCompanies,
    fetchNextPage: fetchMoreCompanies,
    isLoading: isLoadingCompanies,
    isFetchingNextPage: isFetchingMoreCompanies,
    isError: companiesError,
    refetch: refetchCompanies,
  } = useInfiniteQuery({
    queryKey: ['dual-education-companies', locale, me?.authToken ?? null],
    queryFn: ({ pageParam = 1 }) =>
      fetchCompanies({
        locale,
        authToken: me?.authToken,
        query: {
          order: 'desc',
          orderBy: 'id',
          results: 8,
          page: pageParam,
          where: { tags: { in: [4] } },
        },
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: isInitialized,
  });

  return (
    <div className="custom-section mt-8">
      <h1 className="text-xl font-bold md:text-2xl lg:text-3xl">Duálne vzdelávanie</h1>
      <div className="custom-container flex flex-col gap-4">
        <div className="flex w-full items-center justify-between px-6 md:px-0">
          <h1 className="text-lg font-bold md:text-xl">Ponuky duálneho vzdelávania</h1>
          <Button asChild>
            <Link
              prefetch={false}
              href={'/ponuky?order=desc&orderBy=published_at&dual_education=true'}
            >
              <p>Všetky duálne vzdelávania</p>
              <GoArrowRight size={24} className="text-white" />
            </Link>
          </Button>
        </div>
        {offersError && <PortalContentError onRetry={() => refetchOffers()} />}
        <div className="grid w-full grid-cols-1 items-center justify-center gap-0 gap-y-4 sm:grid-cols-2 md:gap-2 lg:grid-cols-3 xl:grid-cols-4">
          {!isInitialized || isLoadingOffers
            ? // Show 8 skeleton cards while loading or when not initialized
              Array.from({ length: 8 }).map((_, index) => <JobOfferCardSmallSkeleton key={index} />)
            : offers?.data?.map(offer => <JobOfferCardSmall offer={offer} key={offer.slug} />)}
        </div>
        <h2 className="mt-8 text-lg font-bold md:text-xl">
          Spoločnosti zapojené do duálneho vzdelávania
        </h2>
        {companiesError && <PortalContentError onRetry={() => refetchCompanies()} />}
        {isInitialized &&
          !isLoadingCompanies &&
          !companiesError &&
          companiesPages &&
          companiesPages.pages.every(page => page.data.length === 0) && (
            <p>Momentálne nie sú k dispozícii žiadne firmy zapojené do duálneho vzdelávania.</p>
          )}
        <div className="grid w-full grid-cols-1 items-center justify-center gap-0 gap-y-4 sm:grid-cols-2 md:gap-2 lg:grid-cols-3 xl:grid-cols-4">
          {!isInitialized || isLoadingCompanies
            ? Array.from({ length: 8 }).map((_, index) => <CompanyCardSmallSkeleton key={index} />)
            : companiesPages?.pages
                .flatMap(page => page.data)
                .map(company => <CompanyCardSmall company={company} key={company.slug} />)}
        </div>
        {hasMoreCompanies && (
          <div className="flex w-full justify-center">
            <Button onClick={() => fetchMoreCompanies()} disabled={isFetchingMoreCompanies}>
              {isFetchingMoreCompanies ? 'Načítavam...' : 'Načítať ďalšie'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Page;
